import Box from "@mui/material/Box";
import Select, { SelectOption } from "shared/components/Form/Select";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { COLOR_DARK_RED } from "shared/theme/colors";
import { DeleteIcon } from "shared/theme/icons/DeleteIcon";
import { TagMatchingType } from "entities/modules/types";
import { TagOption } from "widgets/ModuleTagsWidget/type";
import TagsAutocomplete from "./components/TagsAutocomplete";
import { tagMatchingOptions } from "./constants";
import TagsSelectSkeleton from "./skeleton";
import { StyledIconButton } from "./styled";

type TagTypeOption = {
  value: string;
  label: string;
};

type Props<TagType> = {
  selectedTagType: TagType | null;
  selectedPrimaryTagId: string | null;
  tagTypeOptions: TagTypeOption[];
  primaryTagOptions: SelectOption[];
  selectedTags: TagOption[];
  secondaryTagOptions: TagOption[];
  onTagTypeChange: (tagType: TagType) => void;
  onChangePrimaryTag: (primaryTagId: string) => void;
  onDeleteTagBlock: () => void;
  onSecondaryTagsChange: (tags: TagOption[]) => void;
  deletable?: boolean;
  isLoadingTags?: boolean;
  isVisibleMatchingType?: boolean;
  matchingType?: TagMatchingType;
  onMatchingTypeChange?: (matchingType: TagMatchingType) => void;
};

const TagGroup = <TagType extends string>(props: Props<TagType>) => {
  const {
    selectedTagType,
    selectedTags,
    secondaryTagOptions,
    tagTypeOptions,
    onTagTypeChange,
    primaryTagOptions,
    selectedPrimaryTagId,
    onChangePrimaryTag,
    onDeleteTagBlock,
    onSecondaryTagsChange,
    isLoadingTags,
    isVisibleMatchingType,
    matchingType,
    onMatchingTypeChange,
  } = props;

  return (
    <div>
      <Box
        display="grid"
        gridTemplateColumns={isVisibleMatchingType ? "30% 1fr" : "1fr"}
        gap={1}
      >
        {isVisibleMatchingType && (
          <Select
            options={tagMatchingOptions}
            handleChange={(value) =>
              onMatchingTypeChange?.(value as TagMatchingType)
            }
            label="Match type"
            value={matchingType || ""}
          />
        )}
        <Select
          options={tagTypeOptions}
          handleChange={(value) => onTagTypeChange(value as TagType)}
          label="Tag Type"
          placeholder="Select a type"
          value={selectedTagType || ""}
        />
      </Box>
      <FlexCenter gap={1} mb="1rem" mt="0.5rem">
        <Select
          value={selectedPrimaryTagId || ""}
          options={primaryTagOptions}
          handleChange={(value) => {
            onChangePrimaryTag(value as string);

            if (selectedPrimaryTagId !== value) {
              onSecondaryTagsChange([]);
            }
          }}
          label="Primary"
          placeholder="Select a tag"
          disabled={!selectedTagType}
        />
      </FlexCenter>
      <div>
        <FlexCenter width="100%" mt="0.5rem">
          {isLoadingTags && <TagsSelectSkeleton />}
          {!isLoadingTags && (
            <TagsAutocomplete
              value={selectedTags}
              options={secondaryTagOptions}
              onChange={onSecondaryTagsChange}
              placeholder="Add a tag..."
              loading={isLoadingTags ?? false}
              label="Secondary"
              disabled={!selectedTagType || !selectedPrimaryTagId}
            />
          )}
        </FlexCenter>
      </div>
      <StyledIconButton onClick={onDeleteTagBlock}>
        <DeleteIcon size={22} color={COLOR_DARK_RED} />
        Remove
      </StyledIconButton>
    </div>
  );
};

export default TagGroup;
